import { generateUniqueId, toBase64, getLocalStorage } from './Utils';

/**
 * Dynamic Request Builder
 *
 * This utility builds API requests dynamically based on product configuration
 * received from the backend, eliminating the need for hardcoded request configs.
 */

// Registry of available transform functions
const transformFunctions = {
  toBase64: async (value) => await toBase64(value),
  generateUniqueId: () => generateUniqueId(),
  none: (value) => value,
};

// Registry of available source functions
const sourceFunctions = {
  generateUniqueId: () => generateUniqueId(),
  getCurrentSide: (context) => {
    // Returns 'front' or 'back' based on the current active tab
    return context?.activeTab || 'front';
  },
};

/**
 * Builds request body dynamically based on product configuration
 *
 * @param {Object} config - Product configuration from backend
 * @param {Object} context - Context data (frontImage, backImage, selectedFile, activeTab, etc.)
 * @returns {Promise<Object>} - Constructed request body
 */
export const buildDynamicRequestBody = async (config, context) => {
  const { requestBodySchema } = config;
  const { frontImage, backImage, selectedFile, activeTab, uploadedFiles } =
    context;

  const requestBody = {};

  for (const field of requestBodySchema) {
    const { fieldName, source, transform, defaultValue, functionName } = field;

    let value;

    // Determine the source of the value
    switch (source) {
      case 'file':
      case 'Upload':
        // ✅ NEW: Check if this field exists in uploadedFiles (dynamic upload)
        if (uploadedFiles && uploadedFiles[fieldName]) {
          value = uploadedFiles[fieldName];
        } else {
          // Fallback to legacy logic
          // Use the appropriate file based on activeTab
          value = activeTab === 'front' ? frontImage : backImage;
          // For single mode, use selectedFile
          if (!value) value = selectedFile;
        }
        break;

      case 'constant':
        value = defaultValue;
        break;

      case 'function':
        // Call the specified function with context
        if (sourceFunctions[functionName]) {
          value = sourceFunctions[functionName](context);
        }
        break;

      default:
        value = null;
    }

    // Apply transformation if specified
    if (transform && transformFunctions[transform]) {
      value = await transformFunctions[transform](value);
    }

    requestBody[fieldName] = value;
  }

  return requestBody;
};

/**
 * Builds headers dynamically based on product configuration
 *
 * @param {Object} config - Product configuration from backend
 * @param {Object} currentProduct - Current product data (contains clientKey, etc.)
 * @returns {Object} - Constructed headers
 */
export const buildDynamicHeaders = (config, currentProduct) => {
  const { headers } = config;
  const headersObj = {};

  // Only iterate if headers array exists and is not empty
  if (headers && Array.isArray(headers)) {
    for (const header of headers) {
      const { headerName, source, value } = header;

      switch (source) {
        case 'clientKey':
          headersObj[headerName] = currentProduct?.clientKey;
          break;

        case 'token':
          headersObj[headerName] = `Bearer ${getLocalStorage('token')}`;
          break;

        case 'constant':
          headersObj[headerName] = value;
          break;

        default:
          headersObj[headerName] = '';
      }
    }
  }

  return headersObj;
};

/**
 * Gets the complete API endpoint based on configuration
 *
 * @param {Object} config - Product configuration from backend
 * @param {Object} currentProduct - Current product data (contains productId)
 * @returns {string} - Complete API endpoint URL
 */
export const getDynamicApiEndpoint = (config, currentProduct) => {
  const { baseURL } = config;

  // Append productId to baseURL
  const endpoint = `${baseURL}?productId=${currentProduct?.productId}`;

  return endpoint;
};

/**
 * Main function to execute API call with dynamic configuration
 *
 * @param {Object} config - Product configuration from backend
 * @param {Object} currentProduct - Current product data
 * @param {Object} context - Context data (frontImage, backImage, selectedFile, activeTab, mode)
 * @returns {Promise<Object>} - API response
 */
export const executeDynamicApiCall = async (
  config,
  currentProduct,
  context
) => {
  try {
    // Build endpoint
    const endpoint = getDynamicApiEndpoint(config, currentProduct);

    // Build request body
    const body = await buildDynamicRequestBody(config, context);

    // Build headers
    const headers = buildDynamicHeaders(config, currentProduct);

    // Get HTTP method
    const method = config.httpMethod || 'POST';

    console.log('Dynamic API Call:', {
      endpoint,
      method,
      // headers,
      body,
    });

    // Make API call
    const response = await fetch(endpoint, {
      method,
      headers,
      body: JSON.stringify(body),
    });

    return await response.json();
  } catch (error) {
    console.error('Error in dynamic API call:', error);
    throw error;
  }
};

/**
 * Registers a custom transform function
 * Useful for adding product-specific transformations
 *
 * @param {string} name - Name of the transform function
 * @param {Function} fn - The transform function
 */
export const registerTransformFunction = (name, fn) => {
  transformFunctions[name] = fn;
};

/**
 * Registers a custom source function
 * Useful for adding product-specific data sources
 *
 * @param {string} name - Name of the source function
 * @param {Function} fn - The source function
 */
export const registerSourceFunction = (name, fn) => {
  sourceFunctions[name] = fn;
};
